import { useEffect, useState, useMemo } from "react";
import { fetchTrips } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { DestinationBadge } from "../components/DestinationBadge";
import { format, parseISO, startOfWeek, addDays, isSameDay } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar, BarChart3, Hotel } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminReports() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));

  useEffect(() => {
    fetchTrips().then(setTrips).catch(console.error).finally(() => setLoading(false));
  }, []);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const byDay = useMemo(() => {
    return weekDays.map((day) => ({
      day,
      arrivals: trips.filter((t) => isSameDay(parseISO(t.arrival_date), day)),
      departures: trips.filter((t) => isSameDay(parseISO(t.departure_date), day)),
    }));
  }, [weekDays, trips]);

  const lodgingBreakdown = useMemo(() => {
    const map = {};
    trips.forEach((t) => {
      if (!t.accommodation_name) return;
      const key = `${t.accommodation_name} · ${t.destination}`;
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [trips]);

  if (loading) return <div className="p-8 text-zinc-400 text-center">Loading reports...</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-reports-page">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div>
          <p className="label-eyebrow mb-2">Reporting</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight">Arrivals calendar & breakdown</h1>
        </div>

        {/* Calendar */}
        <div className="glass-card p-5 sm:p-6" data-testid="week-calendar">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#F07D00]" />
              <h2 className="font-heading text-lg font-medium text-white">
                Week of {format(weekStart, "MMM d, yyyy")}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeekStart(addDays(weekStart, -7))}
                className="p-2 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10"
                data-testid="prev-week-button"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-semibold text-zinc-300 hover:bg-white/10 hover:text-white"
                data-testid="this-week-button"
              >
                This week
              </button>
              <button
                onClick={() => setWeekStart(addDays(weekStart, 7))}
                className="p-2 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10"
                data-testid="next-week-button"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
            {byDay.map(({ day, arrivals, departures }) => {
              const today = isSameDay(day, new Date());
              return (
                <div
                  key={day.toISOString()}
                  className={`rounded-lg border p-3 min-h-[180px] ${today ? "bg-[#F07D00]/5 border-[#F07D00]/40" : "bg-white/5 border-white/10"}`}
                  data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-[10px] font-semibold tracking-wider uppercase text-zinc-500">{format(day, "EEE")}</div>
                      <div className={`font-heading text-xl font-semibold ${today ? "text-[#F07D00]" : "text-white"}`}>{format(day, "d")}</div>
                    </div>
                    <div className="text-right text-[10px] space-y-0.5">
                      <div className="text-cyan-400 font-semibold">↓ {arrivals.length}</div>
                      <div className="text-orange-400 font-semibold">↑ {departures.length}</div>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {arrivals.slice(0, 3).map((t) => (
                      <Link
                        key={"a-" + t.id}
                        to={`/admin/records/${t.id}`}
                        className="block p-1.5 rounded bg-cyan-500/10 border border-cyan-500/20 text-[11px] leading-tight hover:bg-cyan-500/20"
                      >
                        <div className="text-cyan-300 font-semibold">↓ {t.full_name.split(" ")[0]}</div>
                        <div className="text-zinc-400 truncate">{t.destination}</div>
                      </Link>
                    ))}
                    {departures.slice(0, 2).map((t) => (
                      <Link
                        key={"d-" + t.id}
                        to={`/admin/records/${t.id}`}
                        className="block p-1.5 rounded bg-orange-500/10 border border-orange-500/20 text-[11px] leading-tight hover:bg-orange-500/20"
                      >
                        <div className="text-orange-300 font-semibold">↑ {t.full_name.split(" ")[0]}</div>
                        <div className="text-zinc-400 truncate">{t.destination}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Lodging + Destination split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="glass-card p-5 sm:p-6" data-testid="lodging-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <Hotel className="h-4 w-4 text-[#F07D00]" />
              <h2 className="font-heading text-lg font-medium text-white">Lodging breakdown</h2>
            </div>
            {lodgingBreakdown.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-8">No lodging data</p>
            ) : (
              <div className="space-y-2">
                {lodgingBreakdown.map(([name, count]) => {
                  const max = lodgingBreakdown[0][1];
                  const pct = (count / max) * 100;
                  return (
                    <div key={name}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-zinc-300 truncate">{name}</span>
                        <span className="text-[#F07D00] font-semibold">{count}</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#F07D00] to-[#FF8C1A]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="glass-card p-5 sm:p-6" data-testid="status-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-4 w-4 text-[#F07D00]" />
              <h2 className="font-heading text-lg font-medium text-white">Status distribution</h2>
            </div>
            <StatusDistribution trips={trips} />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusDistribution({ trips }) {
  const dist = {};
  trips.forEach((t) => { dist[t.status] = (dist[t.status] || 0) + 1; });
  const entries = Object.entries(dist).sort((a, b) => b[1] - a[1]);
  const max = entries[0]?.[1] || 1;

  return (
    <div className="space-y-2.5">
      {entries.map(([status, count]) => (
        <div key={status}>
          <div className="flex justify-between items-center mb-1.5">
            <StatusBadge status={status} />
            <span className="text-zinc-300 font-semibold text-sm">{count}</span>
          </div>
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-white/20" style={{ width: `${(count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
